/**
 * Consent API Service
 *
 * Handles terms acceptance and privacy preferences with the backend.
 */

import { getApiClient } from "./client";

export interface ConsentStatus {
  accepted: boolean;
  terms_version: string | null;
  needs_update: boolean;
  password_must_change: boolean;
  approval_status?: "approved" | "pending" | "rejected";
}

export interface ConsentPreferences {
  consent_analytics_enhanced: boolean;
  consent_quality_improvement: boolean;
}

export interface ConsentAcceptPayload extends ConsentPreferences {
  terms_id: string;
  vessatorie_accepted: boolean;
  first_name?: string;
  last_name?: string;
}

export interface ConsentTerms {
  terms_id: string;
  version: string;
  terms_type: string;
  title: string;
  body_html: string;
  summary: string | null;
  existing_consent: ConsentPreferences | null;
}

export const consentApi = {
  /**
   * Check if user has accepted current terms
   */
  async getStatus(): Promise<ConsentStatus> {
    const client = getApiClient();
    const response = await client.get<ConsentStatus>("/consent/status");
    return response.data;
  },

  /**
   * Get published terms for user's plan type
   */
  async getTerms(): Promise<ConsentTerms> {
    const client = getApiClient();
    const response = await client.get<ConsentTerms>("/consent/terms");
    return response.data;
  },

  /**
   * Accept terms with privacy preferences
   */
  async accept(
    termsId: string,
    prefs: ConsentPreferences,
    name?: { first_name: string; last_name: string },
  ): Promise<void> {
    const client = getApiClient();
    await client.post("/consent/accept", {
      terms_id: termsId,
      ...prefs,
      ...name,
    });
  },

  /**
   * Update privacy preferences without re-accepting terms
   */
  async updatePreferences(prefs: ConsentPreferences): Promise<void> {
    const client = getApiClient();
    await client.patch("/consent/preferences", prefs);
  },
};

export default consentApi;
